import React, { useState } from 'react';
import { RotateCcw, AlertTriangle } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { financeAPI } from '../../services/api';

/**
 * 🧹 Hesabı Sıfırla — temiz sayfa (sadece yönetici).
 * Araçlar, kasa, sermaye, gelir/gider, taksit, randevu, sözleşme vb. kalıcı silinir.
 * Kullanıcılar, yetkiler, şubeler, logo ve profil korunur. Müşteriler isteğe bağlı korunur.
 */
const ResetAccountPanel = () => {
  const { user, fetchData, refreshCapital } = useApp();
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [keepCustomers, setKeepCustomers] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  if ((user?.role || 'admin') !== 'admin') return null;

  const close = () => { setOpen(false); setConfirmText(''); setError(''); };

  const run = async () => {
    if (confirmText !== 'SIFIRLA') return;
    setBusy(true); setError(''); setMessage('');
    try {
      await financeAPI.reset(keepCustomers);
      await fetchData?.(true);
      refreshCapital?.();
      setMessage('Hesap sıfırlandı. Ana sayfadan başlangıç sermayenizi yeniden tanımlayabilirsiniz.');
      close();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Sıfırlama yapılamadı. Lütfen tekrar deneyin.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-amber-500/5 border border-amber-500/30 rounded-xl p-5" data-testid="reset-account-panel">
      <div className="flex items-center gap-2 mb-3">
        <RotateCcw size={20} className="text-amber-400" />
        <h3 className="font-semibold text-amber-400">Hesabı Sıfırla (Temiz Sayfa)</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Tüm araçlar, kasa, sermaye, gelir/gider kayıtları, taksitler, randevular ve sözleşmeler
        <strong> kalıcı olarak silinir</strong> ve hesap sıfırdan başlar. Kullanıcılarınız, yetkiler,
        şubeler, logo ve profil bilgileriniz silinmez. Bu işlem geri alınamaz.
      </p>

      {message && <p className="text-sm text-emerald-400 mb-3">{message}</p>}

      {!open ? (
        <button
          onClick={() => { setOpen(true); setMessage(''); }}
          className="px-4 py-2.5 rounded-lg bg-amber-500/10 border border-amber-500/40 text-amber-400 text-sm font-medium hover:bg-amber-500/20 transition-colors flex items-center gap-2"
          data-testid="show-reset-account-btn"
        >
          <RotateCcw size={16} />
          Hesabı Sıfırla
        </button>
      ) : (
        <div className="space-y-3 p-4 bg-amber-500/10 rounded-lg border border-amber-500/30">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={keepCustomers}
              onChange={(e) => setKeepCustomers(e.target.checked)}
              data-testid="reset-keep-customers"
            />
            Müşteri listemi koru (sadece isim/telefon bilgileri kalır)
          </label>
          <p className="text-sm font-medium text-amber-400 flex items-center gap-1">
            <AlertTriangle size={14} />
            Onaylamak için aşağıya <strong>"SIFIRLA"</strong> yazın:
          </p>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
            className="w-full h-11 px-3 bg-background border border-amber-500/50 rounded-lg text-sm outline-none focus:border-amber-400"
            placeholder="SIFIRLA"
            data-testid="reset-confirm-input"
          />
          {error && <p className="text-sm text-rose-400">{error}</p>}
          <div className="flex gap-3">
            <button
              onClick={run}
              disabled={confirmText !== 'SIFIRLA' || busy}
              className="px-4 py-2.5 rounded-lg bg-amber-500 text-black text-sm font-semibold disabled:opacity-40 transition-colors flex items-center gap-2"
              data-testid="confirm-reset-account-btn"
            >
              <RotateCcw size={16} />
              {busy ? 'Sıfırlanıyor…' : 'Kalıcı Olarak Sıfırla'}
            </button>
            <button
              onClick={close}
              className="px-4 py-2.5 rounded-lg border border-border text-sm text-muted-foreground hover:bg-muted transition-colors"
              data-testid="cancel-reset-btn"
            >
              İptal
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResetAccountPanel;
